import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FileText,
  Plus,
  Save,
  RefreshCcw,
  Trash2,
  Pencil,
  ExternalLink,
} from "lucide-react";

const BASE_URL = "https://nissi-india.com"; // change if needed

const LeaveLetter = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  // ✅ required keys
  const finYearId = localStorage.getItem("finYearId") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const userId = localStorage.getItem("userId") || "";

  // ✅ from URL: /payroll/LeaveLetter?employeeId=245&month=2&year=2026
  const qs = new URLSearchParams(window.location.search);
  const employeeId = qs.get("employeeId") || "";
  const month = qs.get("month") || "";
  const year = qs.get("year") || "";

  // list
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // form
  const [editingId, setEditingId] = useState(null);
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);

  const [existingFileName, setExistingFileName] = useState("");
const [existingFileUrl, setExistingFileUrl] = useState("");

  const screenBadge = useMemo(() => "INFRA SCREEN : Leave Letter", []);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const dangerBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ` +
    (selectedIds.length === 0 ? "bg-gray-400 cursor-not-allowed" : "bg-red-600 hover:bg-red-700");

  const actionBtn =
    `inline-flex items-center justify-center gap-2 px-4 py-2 rounded-2xl text-white font-bold shadow ` +
    `transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

const resetForm = () => {
  setEditingId(null);
  setTitle("");
  setFile(null);
  setExistingFileName("");
  setExistingFileUrl("");

  const el = document.getElementById("leaveletter_file_input");
  if (el) el.value = "";
};

  const fetchList = async () => {
    if (!String(finYearId).trim()) return toast.warn("Please select Financial Year.");
    if (!String(employeeId).trim()) return toast.warn("Employee ID missing in URL.");
    if (!String(month).trim() || !String(year).trim()) return toast.warn("Month/Year missing in URL.");

    try {
      setLoading(true);

      // ✅ list uses employeeId only (same as ASP: usp_Get_LeaveLetter EmployeeID)
      const res = await axios.get(`${BASE_URL}/api/leave-letter/list`, {
        params: {
          finYearId: Number(finYearId),
          employeeId: Number(employeeId),
        },
      });

      if (res?.data?.success) {
        setRows(res.data.rows || []);
        setSelectedIds([]);
        setSelectAll(false);
      } else {
        toast.error(res?.data?.message || "Failed to load leave letters.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "List load error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
      setSelectAll(false);
      return;
    }
    const ids = rows.map((r) => Number(r.fld_Letter_ID)).filter(Boolean);
    setSelectedIds(ids);
    setSelectAll(true);
  };

  const openFile = (row) => {
    // Node route: /api/leave-letter/file/:filename
    const fname = row?.fld_Filename || row?.fld_filename;
    if (!fname) return toast.warn("File not available for this letter.");
    window.open(`${BASE_URL}/api/leave-letter/file/${encodeURIComponent(fname)}`, "_blank");
  };

  const handleAddNew = () => {
    resetForm();
    toast.info("Form ready for new letter");
  };

const handleEdit = async (letterId) => {
  if (!String(finYearId).trim()) return toast.warn("Please select Financial Year.");

  try {
    setLoading(true);
    const res = await axios.get(`${BASE_URL}/api/leave-letter/${letterId}`, {
      params: { finYearId: Number(finYearId) },
    });

    if (res?.data?.success && res?.data?.row) {
      const row = res.data.row;

      setEditingId(letterId);
      setTitle(row.fld_Title || "");

      // ✅ keep existing file info
      setExistingFileName(row.fld_Filename || "");
      setExistingFileUrl(row.fileUrl ? `${BASE_URL}${row.fileUrl}` : "");

      // reset newly chosen file
      setFile(null);
      const el = document.getElementById("leaveletter_file_input");
      if (el) el.value = "";

      toast.info("Loaded for editing");
    } else {
      toast.error(res?.data?.message || "Record not found.");
    }
  } catch (err) {
    toast.error(err?.response?.data?.message || err?.message || "Edit load error");
  } finally {
    setLoading(false);
  }
};

  const handleSave = async (e) => {
    e?.preventDefault?.();

    if (!String(finYearId).trim()) return toast.warn("Please select Financial Year.");
    if (!String(branchId).trim()) return toast.warn("branchId missing in localStorage.");
    if (!String(userId).trim()) return toast.warn("userId missing in localStorage.");

    if (!String(employeeId).trim()) return toast.warn("Employee ID missing in URL.");
    if (!String(month).trim() || !String(year).trim()) return toast.warn("Month/Year missing in URL.");

    if (!title.trim()) return toast.warn("Please Enter Topic..");

    try {
      setLoading(true);

      const fd = new FormData();
      fd.append("title", title.trim());
      fd.append("employeeId", String(employeeId));
      fd.append("month", String(month));
      fd.append("year", String(year));
      fd.append("branchId", String(branchId));
      fd.append("loginUserId", String(userId)); // ✅ as you said
      if (file) fd.append("file", file);

      if (editingId) {
        const res = await axios.put(`${BASE_URL}/api/leave-letter/${editingId}`, fd, {
          params: { finYearId: Number(finYearId) },
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (res?.data?.success) {
          toast.success(res?.data?.message || "Updated Successfully");
          resetForm();
          await fetchList();
        } else {
          toast.error(res?.data?.message || "Update failed");
        }
      } else {
        const res = await axios.post(`${BASE_URL}/api/leave-letter`, fd, {
          params: { finYearId: Number(finYearId) },
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (res?.data?.success) {
          toast.success(res?.data?.message || "Inserted Successfully");
          resetForm();
          await fetchList();
        } else {
          toast.error(res?.data?.message || "Insert failed");
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Save error");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (!String(finYearId).trim()) return toast.warn("Please select Financial Year.");
    if (selectedIds.length === 0) return toast.warn("Please select any one");

    const ok = window.confirm("Are you sure to delete ?");
    if (!ok) return;

    try {
      setLoading(true);
      const res = await axios.post(
        `${BASE_URL}/api/leave-letter/delete-many`,
        { ids: selectedIds },
        { params: { finYearId: Number(finYearId) } }
      );

      if (res?.data?.success) {
        toast.success(res?.data?.message || "Deleted Successfully");
      } else {
        toast.error(res?.data?.message || "Delete failed");
      }

      await fetchList();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Delete error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">LEAVE LETTER</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div
                className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${
                  isDark ? "bg-white/10" : "bg-gray-100"
                }`}
              >
                <FileText className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Leave Letter Listing / Entry
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Add / Edit / Delete / View File
                </div>
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {screenBadge}
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"
                }`}
              >
                {rows.length} Records
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-gray-800/60 text-gray-200" : "bg-gray-100 text-gray-700"
                }`}
              >
                Emp: <b>{employeeId || "-"}</b> | {month || "-"} / {year || "-"}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={fetchList} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>

              <button onClick={handleAddNew} className={primaryBtn} disabled={loading}>
                <Plus className="w-4 h-4" />
                Add New
              </button>

              <button onClick={handleDeleteSelected} className={dangerBtn} disabled={loading || selectedIds.length === 0}>
                <Trash2 className="w-4 h-4" />
                Delete Selected
              </button>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSave} className={cardBase + " p-6 mb-6"}>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
              <div className="text-lg font-extrabold">
                {editingId ? `Edit Letter (ID: ${editingId})` : "New Letter"}
              </div>

              <div className="flex gap-3">
                <button type="button" onClick={resetForm} className={ghostBtn} disabled={loading}>
                  <RefreshCcw className="w-4 h-4" />
                  Clear
                </button>
                <button type="submit" className={primaryBtn} disabled={loading}>
                  <Save className="w-4 h-4" />
                  {editingId ? "Update" : "Save"}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className={labelBase}>
                  Topic <span className="text-red-500">*</span>
                </label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter Topic..."
                  className={`${inputBase} mt-2`}
                />
              </div>

              <div>
                <label className={labelBase}>Upload Leave Letter (optional)</label>
                <input
                  id="leaveletter_file_input"
                  type="file"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className={`${inputBase} mt-2`}
                />
                <div className={`text-xs mt-2 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  If you don&apos;t choose file while editing, it will update topic only.
                </div>
              </div>
            </div>
          </form>

          {/* Table */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-bold">S.No</th>
                    <th className="px-4 py-4 text-center font-bold">File / Topic</th>
                    <th className="px-4 py-4 text-center font-bold">Actions</th>
                    <th className="px-4 py-4 text-center font-bold">
                      <div className="flex items-center justify-center gap-2">
                        Select All
                        <input type="checkbox" checked={selectAll} onChange={handleSelectAll} />
                      </div>
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: 6 }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        {Array.from({ length: 4 }).map((__, c) => (
                          <td key={c} className="px-4 py-4">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={4}
                        className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}
                      >
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => {
                      const letterId = Number(r.fld_Letter_ID);
                      const isChecked = selectedIds.includes(letterId);

                      return (
                        <tr key={letterId || idx} className={`${isDark ? "bg-gray-900" : "bg-white"} hover:${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {idx + 1}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                            <div className="flex flex-col items-center gap-1">
                              <div className="font-bold">{r.fld_Title}</div>

                              {r.fld_Filename ? (
                                <button
                                  type="button"
                                  onClick={() => openFile(r)}
                                  className={`inline-flex items-center gap-2 underline font-bold ${isDark ? "text-blue-300" : "text-blue-700"}`}
                                >
                                  <ExternalLink className="w-4 h-4" />
                                  Open File
                                </button>
                              ) : (
                                <div className={`${isDark ? "text-gray-400" : "text-gray-500"}`}>No File</div>
                              )}
                            </div>
                          </td>

                          <td className="px-4 py-4 text-center">
                            <button
                              type="button"
                              onClick={() => handleEdit(letterId)}
                              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-bold rounded-2xl hover:bg-blue-700 transform hover:-translate-y-0.5 transition-all shadow"
                            >
                              <Pencil className="w-4 h-4" />
                              Edit
                            </button>
                          </td>

                          <td className="px-4 py-4 text-center">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => toggleSelect(letterId)}
                              className="w-4 h-4"
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Bottom */}
          <div className="mt-6 flex flex-wrap gap-3 justify-end">
            <button
              type="button"
              className={ghostBtn}
              onClick={() => window.close()}
              title="If opened in new tab"
            >
              Close Tab
            </button>
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default LeaveLetter;